import type { BranchChangesets } from '@festina/configuration-general-types/configuration'
import type { BranchKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useQuery } from '@tanstack/react-query'

import { isConfigurationServerService } from '../../../hooks/helpers'
import { useService } from '../../../hooks/useService'
import { fetchAllBranchChangesetsKey } from '../keys'

export const useFetchBranchChangesets = ({
  enabled,
  branchKey,
  includeBaseChangesets,
  shouldIncludeExtrinsicParameterValues,
}: {
  enabled: boolean
  branchKey: Optional<BranchKey>
  includeBaseChangesets: boolean
  shouldIncludeExtrinsicParameterValues: boolean
}) => {
  const componentName = branchKey?.server
  const branchName = branchKey?.name ?? undefined
  const service = useService(componentName, branchName)

  // We intentionally use useQuery in order to avoid suspense from kicking in inside the branches panel,
  // making it snappier for users to click through branch in the branches tree.
  return useQuery({
    enabled: !!enabled && !!componentName && !!branchKey && !!service,
    queryKey: componentName
      ? fetchAllBranchChangesetsKey({
          componentName,
          branchName,
          isFromConfigurationServer: service
            ? isConfigurationServerService(service)
            : false,
          includeBaseChangesets,
          shouldIncludeExtrinsicParameterValues,
        })
      : [],
    queryFn: (): Promise<Optional<BranchChangesets>> => {
      assertNotEmpty(service)
      assertNotEmpty(componentName)

      try {
        return service.api.ConfigurationRepositoryBranchesService.branchServiceGetGetBranchBlockChangesets(
          {
            componentName: componentName.toLocaleLowerCase(),
            branchName: branchName || '',
            includeBaseChangesets,
            skipLargeOperationTranslation: true,
            shouldIncludeExtrinsicParameterValues,
          },
        )
      } catch {
        return Promise.resolve(null)
      }
    },
    staleTime: Infinity,
  })
}
